import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BrandHeader, MIN_SEARCH_LENGTH } from '@/components/BrandHeader/BrandHeader';
import { CashbackBannerController } from '@/components/CashbackBanner/CashbackBannerController';
import { ItemCard } from '@/components/ItemCard/ItemCard';
import { useOutletKitchen } from '@/components/LinkToMenu/useOutletKitchen';
import MenuCategories from '@/components/MenuCategories/MenuCategories';
import MenuCategoriesBottomSheet from '@/components/MenuCategories/MenuCategoriesBottomSheet';
import { OrderPromptModal } from '@/components/OrderPromptModal/OrderPromptModal';
import BrandHeaderSkeleton from '@/components/Skeletons/BrandHeaderSkeleton';
import MenuItemsListingSkeleton from '@/components/Skeletons/MenuItemsListingSkeleton';
import { MenuViewProvider, useMenuView } from '@/context/MenuViewContext/MenuViewContext';
import { useBranchMenus } from '@/hooks/useBranchMenus';
import { useLanguage } from '@/hooks/useLanguage';
import { useLocale } from '@/hooks/useLocale';
import useValidateQueryParams from '@/hooks/useValidateQueryParams';
import { BrandCategory } from '@/interfaces/Menu';
import { sendBusinessEvent } from '@/screens/Checkout/monitoring';
import { useBranding } from '@/utils/branding';
import styles from './MenuItemsListing.module.scss';

const MenuItemsListingContent = () => {
  const { queryParams } = useValidateQueryParams();
  const { showAppPromptModal, showMenuAddToCart } = useBranding();
  const { outletId, brandId, tableNumber, counterNumber, origin } = queryParams();
  const { kitchenId, pointOfSaleId, isLoading } = useOutletKitchen(outletId);
  const menuViewedSentRef = useRef(false);
  const { isRTL } = useLanguage();
  const locale = useLocale();
  const {
    state: { isGrid },
  } = useMenuView();

  const { data: branchMenus } = useBranchMenus({
    brandId: brandId || '',
    kitchenId: kitchenId || '',
    pointOfSaleId: pointOfSaleId || '',
    locale,
  });
  const itemRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [activeCategory, setActiveCategory] = useState<BrandCategory | null>(null);
  const activeCategoryRef = useRef(activeCategory);
  activeCategoryRef.current = activeCategory;
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const scrollDirectionRef = useRef<'up' | 'down'>('down');
  const [isOpenCategoriesSheet, setIsOpenCategoriesSheet] = useState(false);
  const [isCashbackBannerHidden, setIsCashbackBannerHidden] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingCategoryToScroll, setPendingCategoryToScroll] = useState<BrandCategory | null>(
    null
  );
  const [isOrderPromptModalOpen, setIsOrderPromptModalOpen] = useState(false);

  const toggleCashbackBanner = useCallback(() => {
    setIsCashbackBannerHidden(prev => !prev);
  }, []);

  const handleSearch = useCallback((searchTerm: string) => {
    setSearchTerm(searchTerm);
  }, []);

  // Handle add to cart button click - opens order prompt modal
  const handleAddToCart = useCallback(() => {
    setIsOrderPromptModalOpen(true);
  }, []);

  // Send menu viewed business event
  useEffect(() => {
    if (branchMenus && !menuViewedSentRef.current) {
      sendBusinessEvent('kitopay.menu.viewed', {
        screen: 'menu',
        tableNumber,
        counterNumber,
        origin: origin || 'direct',
      });
      menuViewedSentRef.current = true;
    }
  }, [branchMenus, tableNumber, counterNumber, origin]);

  const categories = useMemo(
    () =>
      branchMenus?.positions
        ?.map(position => position.brandCategory)
        ?.filter((category, index, self) => index === self.findIndex(c => c.id === category.id)),
    [branchMenus]
  );

  const categoriesWithNumberOfItems = useMemo(
    () =>
      categories?.map(category => ({
        ...category,
        numberOfItems:
          branchMenus?.positions?.filter(position => position.brandCategory.id === category.id)
            .length || 0,
      })),
    [categories, branchMenus]
  );

  const groupedPositions = useMemo(
    () =>
      branchMenus?.positions
        ?.filter(position => {
          if (searchTerm === '') return true;
          return position.name.toLowerCase().includes(searchTerm.toLowerCase());
        })
        ?.reduce(
          (acc, position) => {
            const categoryId = position.brandCategory.id;
            if (!acc[categoryId]) {
              acc[categoryId] = {
                category: position.brandCategory,
                positions: [],
              };
            }
            acc[categoryId].positions.push(position);
            return acc;
          },
          {} as Record<string, { category: BrandCategory; positions: typeof branchMenus.positions }>
        ) || {},
    [branchMenus, searchTerm]
  );

  // Intersection Observer to track visible categories
  useEffect(() => {
    if (!categories || categories.length === 0) return;

    let lastScrollY = window.scrollY;

    // Track scroll direction
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      scrollDirectionRef.current = currentScrollY > lastScrollY ? 'down' : 'up';
      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll);

    // Create observer with dynamic rootMargin based on scroll direction
    const createObserver = (direction: 'up' | 'down') => {
      return new IntersectionObserver(
        entries => {
          if (isUserScrolling) return;

          const visibleCategories = entries
            .filter(entry => entry.isIntersecting)
            .sort((a, b) => {
              const aRect = a.boundingClientRect;
              const bRect = b.boundingClientRect;
              return aRect.top - bRect.top;
            });

          if (visibleCategories.length > 0) {
            const firstVisibleCategory = visibleCategories[0];
            const categoryId = firstVisibleCategory.target.getAttribute('data-category-id');
            const category = categories.find(cat => cat.id === categoryId);

            if (category && category.id !== activeCategoryRef.current?.id) {
              setActiveCategory(category);
            }
          }
        },
        {
          rootMargin: direction === 'down' ? '-20% 0px -60% 0px' : '-30% 0px -40% 0px',
          threshold: 0,
        }
      );
    };

    let currentObserver = createObserver(scrollDirectionRef.current);

    // Observe all category headers
    Object.values(groupedPositions).forEach(({ category }) => {
      const element = itemRefs.current[category.id];
      if (element) {
        element.setAttribute('data-category-id', category.id);
        currentObserver.observe(element);
      }
    });

    // Update observer when scroll direction changes
    const updateObserver = () => {
      const newDirection = scrollDirectionRef.current;
      const newObserver = createObserver(newDirection);

      // Re-observe all elements with new observer
      Object.values(groupedPositions).forEach(({ category }) => {
        const element = itemRefs.current[category.id];
        if (element) {
          currentObserver.unobserve(element);
          newObserver.observe(element);
        }
      });

      currentObserver.disconnect();
      currentObserver = newObserver;
    };

    // Debounced observer update
    let updateTimeout: NodeJS.Timeout;
    const debouncedUpdateObserver = () => {
      clearTimeout(updateTimeout);
      updateTimeout = setTimeout(updateObserver, 100);
    };

    // Listen for scroll direction changes
    const originalHandleScroll = handleScroll;
    const handleScrollWithUpdate = () => {
      const previousDirection = scrollDirectionRef.current;
      originalHandleScroll();
      const newDirection = scrollDirectionRef.current;

      if (previousDirection !== newDirection) {
        debouncedUpdateObserver();
      }
    };

    window.removeEventListener('scroll', handleScroll);
    window.addEventListener('scroll', handleScrollWithUpdate);

    return () => {
      currentObserver.disconnect();
      window.removeEventListener('scroll', handleScrollWithUpdate);
      clearTimeout(updateTimeout);
    };
  }, [categories, groupedPositions, isUserScrolling]);

  const handleCategorySelect = useCallback(
    (category: BrandCategory, shouldScroll: boolean = true) => {
      if (!shouldScroll) return;

      // Immediately set active category when user clicks
      setActiveCategory(category);

      // Set user scrolling flag to prevent automatic updates
      setIsUserScrolling(true);

      // Clear any existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      const element = itemRefs.current[category.id];
      if (element) {
        // Check if this is the first category
        const isFirstCategory = categories && categories[0]?.id === category.id;

        if (isFirstCategory) {
          // For first category, scroll to top to show menu at the very top
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        } else {
          // For other categories, determine if menu is sticky based on current scroll position
          const elementPosition = element.offsetTop;
          const currentScrollY = window.scrollY;

          const isTabletOrDesktop = window.innerWidth >= 768;
          const menuHeight = isTabletOrDesktop ? 64 : 44;

          const menuStickyThreshold = 352 + menuHeight;
          const isMenuCurrentlySticky = currentScrollY >= menuStickyThreshold;

          const nonStickyOffset = isTabletOrDesktop ? 150 : 120;

          // Adjust offset based on menu state and screen size
          const offset = isMenuCurrentlySticky ? menuHeight + 20 : nonStickyOffset;
          const offsetPosition = elementPosition - offset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });
        }
      }

      // Re-enable automatic updates after scroll completes
      scrollTimeoutRef.current = setTimeout(() => {
        setIsUserScrolling(false);
      }, 1500);
    },
    [categories]
  );

  // Set first category as active on initial load
  useEffect(() => {
    if (categories && categories.length > 0 && !activeCategory) {
      setActiveCategory(categories[0]);
    }
  }, [categories, activeCategory]);

  const handleBottomSheetClosed = useCallback(() => {
    if (pendingCategoryToScroll) {
      handleCategorySelect(pendingCategoryToScroll, true);
      setPendingCategoryToScroll(null);
    }
  }, [pendingCategoryToScroll, handleCategorySelect]);

  if (isLoading)
    return (
      <>
        <BrandHeaderSkeleton />
        <MenuItemsListingSkeleton />
      </>
    );

  if (!outletId || !brandId) {
    return <div>No outletId or brandId</div>;
  }

  return (
    <div className={`${styles.menu} ${isRTL ? styles.rtl : ''}`} data-testid="menu-listing">
      {brandId && kitchenId && pointOfSaleId && (
        <BrandHeader
          brandId={brandId}
          kitchenId={kitchenId}
          pointOfSaleId={pointOfSaleId}
          outletId={outletId}
          tableNumber={tableNumber}
          counterNumber={counterNumber}
          origin={origin || undefined}
          onSearch={handleSearch}
        />
      )}

      {searchTerm.length <= MIN_SEARCH_LENGTH && (
        <MenuCategories
          categories={categories || []}
          onCategorySelect={handleCategorySelect}
          activeCategory={activeCategory}
          onOpenCategoriesSheet={() => setIsOpenCategoriesSheet(!isOpenCategoriesSheet)}
        />
      )}

      <div
        className={`${styles.listing} ${isGrid && styles.gridView}`}
        data-testid="menu-items-listing"
        data-view={isGrid ? 'grid' : 'list'}
      >
        {Object.values(groupedPositions).map(({ category, positions }) => (
          <div key={category.id} className={styles.category}>
            <h2
              className={`${styles.categoryHeader} ${isGrid && styles.gridCategoryHeader}`}
              ref={el => {
                itemRefs.current[category.id] = el;
              }}
            >
              {category.name}
            </h2>
            <div className={styles.categoryItems}>
              {positions?.map(position => (
                <ItemCard
                  key={position.id}
                  position={position}
                  menuId={branchMenus?.menuId || ''}
                  toggleCashbackBanner={toggleCashbackBanner}
                  onAddToCart={showMenuAddToCart ? handleAddToCart : undefined}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      {isOpenCategoriesSheet && (
        <MenuCategoriesBottomSheet
          categories={categoriesWithNumberOfItems || []}
          onClose={() => {
            setIsOpenCategoriesSheet(false);
            handleBottomSheetClosed();
          }}
          onConfirm={category => {
            setPendingCategoryToScroll(category);
          }}
          activeCategory={activeCategory}
        />
      )}

      {!isOpenCategoriesSheet && !isCashbackBannerHidden && <CashbackBannerController />}

      {showAppPromptModal && (
        <OrderPromptModal
          isOpen={isOrderPromptModalOpen}
          onClose={() => setIsOrderPromptModalOpen(false)}
        />
      )}
    </div>
  );
};

const MenuItemsListing = () => {
  return (
    <MenuViewProvider>
      <MenuItemsListingContent />
    </MenuViewProvider>
  );
};

export default MenuItemsListing;
